import { useState, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Building2, Check, ChevronDown } from 'lucide-react';
import { useWorkspaceStore } from '@/store/workspace.store';

/**
 * SELECTOR DE NEGOCIO (D2).
 *
 * Va arriba del todo, a la izquierda, porque es el contexto de TODO lo demás:
 * lo que se ve en cada pantalla del panel es lo de ESTE negocio. No hay vista
 * unificada — cambiar acá cambia la aplicación entera.
 *
 * D5 · No se pinta si el servidor dice que no toca (`mostrarSelector`), que es
 * el caso de una instancia de cliente (`MULTI_WORKSPACE_ENABLED=false`) o de un
 * usuario con un solo negocio. Ahí la interfaz queda exactamente como estaba
 * antes de que existieran los workspaces.
 *
 * Al cambiar de negocio se INVALIDA toda la caché de consultas: los datos del
 * anterior no pueden quedar en pantalla ni un instante. Es lo que hace que el
 * cambio se sienta como entrar a otro sistema, que es lo que es.
 */
export function WorkspaceSelector() {
  const items = useWorkspaceStore((s) => s.items);
  const activeId = useWorkspaceStore((s) => s.activeId);
  const mostrarSelector = useWorkspaceStore((s) => s.mostrarSelector);
  const setActive = useWorkspaceStore((s) => s.setActive);
  const queryClient = useQueryClient();
  const [abierto, setAbierto] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  if (!mostrarSelector || items.length === 0) return null;

  const activo = items.find((w) => w.id === activeId) ?? items[0];

  const cambiar = async (id: string) => {
    setAbierto(false);
    if (id === activeId) return;
    setActive(id);

    // Todo lo cargado pertenece al negocio anterior: se tira entero. Sin esto,
    // el panel muestra los datos de un negocio con el nombre de otro arriba —
    // que es exactamente el bug que apareció al probarlo en el navegador.
    //
    // `clear()` NO basta: vacía la caché pero no vuelve a pedir lo que está
    // montado en pantalla, así que la vista se quedaba con lo viejo.
    // `resetQueries()` hace las dos cosas: descarta y RE-CONSULTA lo activo,
    // ya con la cabecera del negocio nuevo.
    queryClient.clear();
    await queryClient.resetQueries();
  };

  return (
    <div className="relative min-w-0 max-w-[240px]" ref={ref} data-tour="workspace">
      <button
        onClick={() => setAbierto((v) => !v)}
        className="mac-capsula min-w-0 max-w-full justify-start px-3.5 text-sm font-medium"
        aria-label="Cambiar de negocio"
        aria-haspopup="listbox"
        aria-expanded={abierto}
      >
        <Building2 className="h-4 w-4 shrink-0 text-acento-icono" strokeWidth={1.75} />
        <span className="truncate">{activo?.name ?? 'Negocio'}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-500" />
      </button>

      {abierto && (
        <div
          role="listbox"
          className="mac-menu absolute left-0 z-50 mt-2 w-64 max-w-[calc(100vw-1.5rem)]"
        >
          <p className="px-[9px] py-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
            Tus negocios
          </p>
          {items.map((w) => (
            <button
              key={w.id}
              role="option"
              aria-selected={w.id === activeId}
              onClick={() => cambiar(w.id)}
              className="mac-menu-fila justify-between"
            >
              <span className="truncate">{w.name}</span>
              {w.id === activeId && <Check className="h-4 w-4 shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
